import { useDispatch, useSelector } from "react-redux";

import ProductImage from "../common/components/Product/ProductImage";
import { Icon } from "../common/components/icon/Icon";
import { Box, IconButton, Stack, styled } from "@mui/material";

import { IconType } from "../common/components/icon/IconType";
import { SimpleStockIndicator } from "../common/components/StockIndicator/StockIndicator";
import { useAddProductToWishlistFlow } from "../common/hooks/useAddProductToWishlistFlow";
import { type RootState } from "../redux/store";
import { selectProductDescriptor } from "../redux/products/productsSelectors";
import { Permission, userHasPermission } from "../utils/permissions";
import { removeFromComparison } from "./redux/actions";
import { IconSize } from "../common/components/icon/IconSize";
import { OldDesignationParameter } from "../common/components/Product/ProductParams";
import { userIsRegistered } from "../utils/roles";
import {
  getListPrice,
  getTariff,
  isProductPMA,
  resolveSmallestPackageFromProductData,
} from "../feature/ProductDescriptor/v1/productDescriptorUtils";
import { NewVersionMessage } from "../common/components/Messages/NewVersionMessage";
import CircleSpinner from "../common/components/CircleSpinner/CircleSpinner";
import { AddToCartService } from "../AddToCart/AddToCartService";
import { canSeeAddToCartButton, canSeeListPrice } from "../utils/features";
import { useTranslation } from "react-i18next";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";
import { calculateSalesCampaign4PDP, useSelectPricingDetails } from "~/SalesCampaign/utils";
import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import { Button } from "~/common/components/Button/Button";
import classes from "./ProductComparisonCard.module.less";
import classNames from "classnames";
import { SideButton } from "~/common/components/Button/SideButton";
import { selectProductIsInStock } from "~/utils/pna/availabilitySelectors";
import { isTariffSurchargeEnabled } from "~/feature/cart/cart/utils/cartUtils";
import { useMemo } from "react";
import { useSalesHelper } from "~/SalesCampaign/salesHelper";
import { ProductDescriptorContext } from "~/feature/Product/ProductContext";
import { Product } from "~/feature/Product";
import { PriceElements, type PriceProps } from "~/common/components/Price/PriceV4";
import { SaleDiscountIndicator } from "~/SalesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";

const WIDTHS = {
  product: "120px",
  gap: "10px",
  brand: "32px",
  manufacturer: "32px",
};

interface Props {
  productCode: string;
}

export const usePCCardLogic = (productCode: string) => {
  const priceReference = getInitialPriceReference();
  const productDescriptor = useSelector((state: RootState) => {
    return selectProductDescriptor(state, {
      productCode,
      priceReference,
    });
  });

  const packCode = resolveSmallestPackageFromProductData(productDescriptor);
  const { data } = useMemo(() => calculateSalesCampaign4PDP({ productDescriptor }), [productDescriptor]);
  const salesData = data?.salePerPack?.[packCode];
  const { price, oldPrice, netPrice, discountFormatted } = useSelectPricingDetails({
    packCode,
    quantity: 1,
    priceReference,
    productCode,
    salesData,
    saleActivated: true,
  });
  const hasSurcharge = (netPrice?.surcharge ?? 0) > 0;

  const showListPrice = canSeeListPrice({
    product: productDescriptor,
    isSpecialPrice: netPrice?.isSpecial ?? false,
    hasSurcharge,
  });

  const listPrice = showListPrice ? getListPrice({ product: productDescriptor, packCode }) : null;

  const tariffSurcharge = isTariffSurchargeEnabled() && getTariff(productDescriptor);

  return {
    price,
    oldPrice,
    netPrice,
    discountFormatted,
    productDescriptor,
    showListPrice,
    listPrice,
    packCode,
    tariffSurcharge,
    hasSurcharge,
  };
};

const ProductComparisonCard = ({ productCode }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    price,
    oldPrice,
    netPrice,
    discountFormatted,
    productDescriptor,
    packCode,
    showListPrice,
    listPrice,
    tariffSurcharge,
    hasSurcharge,
  } = usePCCardLogic(productCode);

  const wishlistButtonVisible = userHasPermission(Permission.ADD_TO_WISHLIST);
  const atcButtonVisible = canSeeAddToCartButton();

  const handleCloseClick = () => {
    dispatch(removeFromComparison(productCode, productDescriptor.base.designation));
  };

  const doAddToCart = () => {
    AddToCartService.openAddToCartWizard(
      {
        productCode: productDescriptor.base.code,
        restrictionQuery: "",
      },
      {
        page: "COMPARISON",
      },
    );
  };

  const [openAddToWishlist, addToWishlistModal] = useAddProductToWishlistFlow({ productInput: productDescriptor.base });
  const onWishlistBtnClick = () => {
    openAddToWishlist();
  };

  const isInStock = selectProductIsInStock(productDescriptor.availability);

  const salesHelper = useSalesHelper(productDescriptor);
  const isProductOnSale = salesHelper.isProductOnSale();
  const isPackageOnSale = salesHelper.isPackageOnSale(packCode);

  const disableWS = window.SCHA.config.disableAllWS;

  const showPrices = !disableWS && userHasPermission(Permission.PRICE);
  const stockVisible = userHasPermission(Permission.AVAILABILITY) && productDescriptor.base.quantityVisible;

  let atcButton;
  let atwButton;

  if (atcButtonVisible) {
    if (isProductPMA(productDescriptor)) {
      atcButton = (
        <div className="scha__comparison-card__on-request-only" onClick={doAddToCart}>
          <Icon type={IconType.InformationCircle} size={IconSize.FONT_24} className={"icon-margin"} />
          <span>{t("product.pma.messages.short")}</span>
        </div>
      );
    } else {
      atcButton = (
        <Button
          size={"medium"}
          startIcon={<Icon type={IconType.Cart} />}
          disabled={disableWS}
          onClick={doAddToCart}
          fullWidth
          className={classNames("p-0", classes.button)}
        >
          {userIsRegistered() ? t("addToCartPopup.add.submitBtn") : t("basket.addToBasket")}
        </Button>
      );
    }
  }

  if (wishlistButtonVisible) {
    if (atcButton) {
      atwButton = (
        <SideButton
          size={"small"}
          icon={IconType.Bookmark}
          color={"secondary"}
          onClick={onWishlistBtnClick}
          className={classNames("scha__comparison-page__add-to-wishlist", classes.button, classes.wishlist)}
        />
      );
    } else {
      atwButton = (
        <Button
          startIcon={<Icon type={IconType.Bookmark} />}
          onClick={onWishlistBtnClick}
          fullWidth
          className={classNames(classes.button, classes.wishlist)}
        >
          {t("page.search.tabular.action.addToWishlist")}
        </Button>
      );
    }
  }

  return (
    <>
      {addToWishlistModal}
      <ProductDescriptorContext value={productDescriptor}>
        <div className="scha__comparison-card">
          <IconButton className="close-icon" color={"primary"} onClick={handleCloseClick}>
            <Icon type={IconType.Close} />
          </IconButton>

          {isProductOnSale && <FloatingSalesTag size={32} />}

          <Stack gap={2} sx={{ height: "100%" }}>
            <div className="product-image">
              <ProductImage product={productDescriptor.base} widths={WIDTHS} />
            </div>
            <Stack gap={0.5}>
              <Stack direction="row" alignItems="center">
                <Product.PreferredIndicator />
                <Product.PdpLink>
                  <Product.DesignationV2 variant="titleLarge" />
                </Product.PdpLink>
              </Stack>

              <Product.MMDChip />

              <Product.ShortDescriptionV2 variant="bodyLarge" />
              <Product.DescriptionV2 />
            </Stack>

            <NewVersionMessage
              className={"scha__comparison-card__new-version-msg"}
              baseProduct={productDescriptor.base}
            />

            <div className="old-designation-wrapper">
              <OldDesignationParameter label={t("productDetailPage.oldDesignation")} product={productDescriptor.base} />
            </div>

            {showPrices && (
              <>
                <PriceElements.Stack>
                  <Stack direction="row" gap={1} alignItems={"center"}>
                    <CurrentPrice
                      price={price}
                      zeroText={t("product.priceOnRequest")}
                      tariffSurcharge={tariffSurcharge}
                      variant="headlineMedium"
                      fontWeight={"bold"}
                      surchargeEnabled={hasSurcharge}
                    />
                    {isPackageOnSale && (
                      <SaleDiscountIndicator.V2
                        discountFormatted={discountFormatted}
                        variant="headlineSmall"
                        sx={{ fontWeight: 400 }}
                      />
                    )}
                  </Stack>
                  {oldPrice && <OldPrice price={oldPrice} />}
                  <PriceElements.NetPriceLabel price={netPrice} />
                </PriceElements.Stack>

                {showListPrice && listPrice && (
                  <Box>
                    <PriceElements.Price price={listPrice} />
                    <PriceElements.ListPriceLabel unit={netPrice?.unit ?? 1} />
                  </Box>
                )}
              </>
            )}

            {stockVisible && (
              <>
                {productDescriptor.availability && (
                  <div className="stock" onClick={doAddToCart}>
                    <SimpleStockIndicator inStock={isInStock} checkAvailability />
                  </div>
                )}
                {productDescriptor.isLoading && <CircleSpinner className={"mt-3"} />}
              </>
            )}

            <Stack direction="row" gap={1} sx={{ mt: "auto" }}>
              {atwButton}
              {atcButton}
            </Stack>
          </Stack>
        </div>
      </ProductDescriptorContext>
    </>
  );
};

export default ProductComparisonCard;

const FloatingSalesTag = styled(SaleTag)({
  position: "absolute",
  top: 0,
  left: 0,
});

const CurrentPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));
